<template>
  <div class="otp-input-root">
    <div class="otp-boxes" :class="{ 'otp-boxes--error': hasError }">
      <input
        v-for="(_, index) in 6"
        :key="index"
        :ref="`otp_${index}`"
        class="otp-box"
        :class="{
          'otp-box--filled': digits[index] !== '',
          'otp-box--focused': focusedIndex === index,
          'otp-box--error': hasError,
        }"
        type="text"
        inputmode="numeric"
        maxlength="1"
        :value="digits[index]"
        autocomplete="one-time-code"
        @focus="onFocus(index)"
        @blur="onBlur"
        @keydown="onKeydown($event, index)"
        @input="onInput($event, index)"
        @paste="onPaste($event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'complete'],

  data() {
    return {
      digits: ['', '', '', '', '', ''],
      focusedIndex: null,
    };
  },

  watch: {
    value(val) {
      this.syncFromValue(val);
    },
  },

  mounted() {
    if (this.value) {
      this.syncFromValue(this.value);
    }
    if (this.autofocus) {
      this.$nextTick(() => this.focusBox(0));
    }
  },

  methods: {
    syncFromValue(val) {
      const clean = String(val || '')
        .replace(/\D/g, '')
        .slice(0, 6);
      this.digits = Array.from({ length: 6 }, (_, i) => clean[i] || '');
    },

    emitValue() {
      const combined = this.digits.join('');
      this.$emit('input', combined);
      if (combined.length === 6) {
        this.$emit('complete', combined);
      }
    },

    focusBox(index) {
      const el = this.$refs[`otp_${index}`];
      if (el && el[0]) el[0].focus();
      else if (el) el.focus();
    },

    onFocus(index) {
      this.focusedIndex = index;
    },

    onBlur() {
      this.focusedIndex = null;
    },

    onInput(event, index) {
      const raw = event.target.value.replace(/\D/g, '');

      if (raw.length > 1) {
        this.distributeDigits(raw, index);
        return;
      }

      const digit = raw.slice(-1); // son rakamı al
      this.$set(this.digits, index, digit);
      this.emitValue();

      if (digit && index < 5) {
        this.focusBox(index + 1);
      }
    },

    onKeydown(event, index) {
      const { key } = event;

      if (key === 'Backspace') {
        event.preventDefault();
        if (this.digits[index] !== '') {
          this.$set(this.digits, index, '');
          this.emitValue();
        } else if (index > 0) {
          this.$set(this.digits, index - 1, '');
          this.emitValue();
          this.focusBox(index - 1);
        }
        return;
      }

      if (key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        this.focusBox(index - 1);
        return;
      }

      if (key === 'ArrowRight' && index < 5) {
        event.preventDefault();
        this.focusBox(index + 1);
        return;
      }

      if (key.length === 1 && !/[0-9]/.test(key) && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);
      if (!text) return;
      this.distributeDigits(text, this.focusedIndex ?? 0);
    },

    distributeDigits(str, startIndex) {
      const chars = str.split('');
      chars.forEach((ch, i) => {
        const pos = startIndex + i;
        if (pos < 6) this.$set(this.digits, pos, ch);
      });
      this.emitValue();
      const nextEmpty = this.digits.findIndex(d => d === '');
      this.focusBox(nextEmpty === -1 ? 5 : nextEmpty);
    },
  },
};
</script>

<style scoped lang="scss">
.otp-input-root {
  display: inline-block;
  position: relative;
}

.otp-boxes {
  display: flex;
  gap: 10px;
  align-items: center;

  &::before {
    display: none;
  }
}

.otp-box {
  width: 48px;
  height: 56px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  background: #ffffff;
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  color: #1a1a1a;
  caret-color: transparent;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  -webkit-appearance: none;
  appearance: none;
  outline: none;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &--filled {
    background: #f7f9ff;
    border-color: rgba(0, 0, 0, 0.25);
  }

  &--focused {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    background: #ffffff;
  }

  &--error {
    border-color: #d32f2f !important;
    box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.12) !important;
    background: #fff5f5;
    animation: otp-shake 0.35s ease;
  }
}

@keyframes otp-shake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-5px);
  }
  40% {
    transform: translateX(5px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(4px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
